import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { CalendarBlankIcon } from '@phosphor-icons/react'
import React, { useState } from 'react'

import { DateRange } from '@/models/helpers'
// Note: import the two formatters directly (not via the `@/utils/date` barrel) so
// this module doesn't transitively pull in unrelated utilities.
import { formatDateOnly } from '@/utils/date/format-date-only'
import { formatDateRange } from '@/utils/date/format-date-range'

import { DatePicker } from '../date-picker'
import { TextInput, TextInputProps } from '../text-input'
import { DATE_PICKER_INPUT_OFFSET } from './date-picker-input.constants'
import * as S from './date-picker-input.styled'
import {
  DatePickerInputProps,
  DatePickerInputRangeProps,
  DatePickerInputSingleProps,
} from './date-picker-input.types'

// Note: The two modes are a discriminated union, so a single destructure can't
// reach both `value` shapes and both handlers. This loosened view lets us pull
// everything out in one place; each field is narrowed back to its real type at
// the use sites below.
type DatePickerInputResolvedProps = Omit<
  DatePickerInputSingleProps & DatePickerInputRangeProps,
  'mode' | 'value' | 'onChange' | 'onRangeChange'
> & {
  mode?: 'single' | 'range'
  value?: Date | DateRange | null
  onChange?: (date: Date | null) => void
  onRangeChange?: (range: DateRange) => void
}

export const DatePickerInput = (props: DatePickerInputProps) => {
  const {
    mode = 'single',
    minDate,
    maxDate,
    timezone,
    size = 'medium',
    disabled = false,
    placeholder,
    value,
    onChange,
    onRangeChange,
    dateFormat,
    dateRangeFormat,
    periodOptions,
    ...textInputProps
  } = props as DatePickerInputResolvedProps

  const [isOpen, setIsOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset(DATE_PICKER_INPUT_OFFSET),
      flip(),
      sizeMiddleware({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          })
        },
      }),
    ],
    onOpenChange: setIsOpen,
    open: isOpen,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'dialog' })

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

  const isSingleMode = mode === 'single'

  const singleValue = isSingleMode ? ((value as Date | null) ?? null) : null
  const rangeValue = isSingleMode ? null : ((value as DateRange | null) ?? null)

  const hasValue = isSingleMode ? !!singleValue : !!(rangeValue && rangeValue[0])

  // Note: consumer-supplied formatters take precedence; otherwise fall back to the
  // app-wide `MM/dd/yyyy` formatting used everywhere a date is shown.
  const formatSingle: (date: Date) => string = dateFormat ?? formatDateOnly
  const formatRange: (range: DateRange) => string = dateRangeFormat ?? formatDateRange

  const displayValue = isSingleMode
    ? singleValue
      ? formatSingle(singleValue)
      : ''
    : rangeValue && rangeValue[0]
      ? formatRange(rangeValue)
      : ''

  const displayPlaceholder = placeholder || (isSingleMode ? 'Select a date' : 'Select a date range')

  const handleSingleChange = (date: Date) => {
    onChange?.(date)
    setIsOpen(false)
  }

  const handleRangeChange = (range: DateRange) => {
    onRangeChange?.(range)
    // Note: keep the calendar open mid-range — only close once both ends are picked.
    if (range[0] && range[1]) {
      setIsOpen(false)
    }
  }

  // Note: the field is pick-only — the date is chosen from the calendar, never
  // typed. The only meaningful text mutation is the clear (X) button, which
  // empties the input; any other (typed/pasted) value is ignored so the display
  // always mirrors the calendar selection.
  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== '') return

    if (isSingleMode) {
      onChange?.(null)
      return
    }

    onRangeChange?.([null, null])
  }

  return (
    <>
      <TextInput
        {...(textInputProps as TextInputProps)}
        {...(getReferenceProps() as React.InputHTMLAttributes<HTMLInputElement>)}
        icon={CalendarBlankIcon}
        dataComponentId="DatePickerInput"
        inputWrapperRef={refs.setReference}
        size={size}
        disabled={disabled}
        placeholder={displayPlaceholder}
        value={displayValue}
        clearable={hasValue}
        // Note: keep the regular (bordered) TextInput look, but make it behave as
        // pick-only — `inputMode="none"` suppresses the mobile keyboard and the
        // transparent caret hides the blinking text cursor, so it reads as a
        // click-to-open field rather than an editable one. (We can't use the
        // `readOnly` prop here: that switches TextInput to its borderless
        // read-only visual state, which we don't want.)
        inputMode="none"
        autoComplete="off"
        style={{ caretColor: 'transparent' }}
        onChange={handleTextChange}
      />

      {isOpen && (
        <FloatingPortal>
          <S.FloatingWrapper ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
            <S.Dropdown>
              {isSingleMode ? (
                <DatePicker
                  value={singleValue}
                  minDate={minDate}
                  maxDate={maxDate}
                  timezone={timezone}
                  mode="single"
                  onChange={handleSingleChange}
                />
              ) : (
                <DatePicker
                  value={rangeValue}
                  minDate={minDate}
                  maxDate={maxDate}
                  timezone={timezone}
                  mode="range"
                  periodOptions={periodOptions}
                  onRangeChange={handleRangeChange}
                />
              )}
            </S.Dropdown>
          </S.FloatingWrapper>
        </FloatingPortal>
      )}
    </>
  )
}
